import AddButton from "@/components/buttons/AddButton";
import { Tooltip } from "@/components/ui/tooltip";
import IdAndNameData from "@/data/IdAndNameData";
import { DataList, Flex, IconButton, Text, useDisclosure } from "@chakra-ui/react";
import { LuPencilLine, LuTrash } from "react-icons/lu";
import TimesheetBalanceDialog from "./TimesheetBalanceDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useEffect, useState } from "react";
import WaitingSpinner from "@/components/WaitingSpinner";
import { formatMinutes, formatOverview } from "@/data/DateTimeUtil";
import { User } from "@/api/User";
import { TimesheetBalance } from "@/api/TimesheetBalance";
import { timesheetService } from "@/service/TimesheetService";


type TimesheetBalanceSectionProps = {
    user: User;
    canEdit?: boolean;
};

const TimesheetBalanceSection = ({ user, canEdit = true }: TimesheetBalanceSectionProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { open: isTimesheetBalanceOpen, onOpen: onTimesheetBalanceOpen, onClose: onTimesheetBalanceClose } = useDisclosure();
    const [timesheetBalanceEntries, setTimesheetBalanceEntries] = useState<TimesheetBalance[]>([]);
    const [editTimesheetBalanceData, setEditTimesheetBalanceData] = useState<TimesheetBalance>(null);
    const { open: isDeleteTimesheetBalanceOpen, onOpen: onDeleteTimesheetBalanceOpen, onClose: onDeleteTimesheetBalanceClose } = useDisclosure();
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);

    useEffect(() => {
        setIsLoading(true);
        if (user) {
            const p = timesheetService.findAllBalance(user?.id).then(r => setTimesheetBalanceEntries(r));
            p.finally(() => setIsLoading(false));
        }
    }, [user]);

    const newTimesheetBalance = () => {
        setEditTimesheetBalanceData(null);
        onTimesheetBalanceOpen();
    }

    const saveTimesheetBalance = (ab: TimesheetBalance) => {
        console.log(ab);
        editTimesheetBalanceData ? timesheetService.updateBalance(ab).then(id => {
            const idx = timesheetBalanceEntries.findIndex(e => e.id === id);
            if (idx >= 0) {
                timesheetBalanceEntries[idx] = ab;
                setTimesheetBalanceEntries([...timesheetBalanceEntries]);
            }
        }) :
            timesheetService.addBalance(ab).then(id => {
                ab.id = id;
                setTimesheetBalanceEntries(
                    [...timesheetBalanceEntries, ab].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                );
            });
        setEditTimesheetBalanceData(null);
        onTimesheetBalanceClose();
    }

    const deleteTimesheetBalance = (id: number) => {
        timesheetService.removeBalance(user?.id, id).then(() => {
            const idx = timesheetBalanceEntries.findIndex(e => e.id === id);
            if (idx >= 0) {
                timesheetBalanceEntries.splice(idx, 1);
                setTimesheetBalanceEntries([...timesheetBalanceEntries]);
            }
        });
        setDeleteData(null);
        onDeleteTimesheetBalanceClose();
    }


    return (
        <>
            {canEdit && <AddButton caption='Neue Anpassung' onClick={newTimesheetBalance} />}
            {isLoading && <WaitingSpinner size='sm' />}
            {timesheetBalanceEntries.length === 0 ?
                <Text textStyle='sm'>keine Einträge</Text> :
                <DataList.Root orientation={{ base: 'vertical', sm: 'horizontal' }} gap={{ base: '4', sm: '0' }}>
                    {timesheetBalanceEntries.map((entry: TimesheetBalance) => {
                        const hours = formatMinutes(entry.minutes);
                        const from = formatOverview(entry.createdAt);
                        return (
                            <DataList.Item key={entry.id} flexDirection={{ sm: 'row' }}>
                                <DataList.ItemLabel>{from}</DataList.ItemLabel>
                                <DataList.ItemValue>{hours}&nbsp;<small>Stunden</small>&nbsp;{entry.comments ?? ''}</DataList.ItemValue>
                                {canEdit && (
                                    <Flex>
                                        <Tooltip content='Bearbeiten'>
                                            <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs" onClick={() => {
                                                setEditTimesheetBalanceData(entry);
                                                onTimesheetBalanceOpen();
                                            }}>
                                                <LuPencilLine />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip content='Löschen'>
                                            <IconButton aria-label="Löschen" colorPalette="red" variant="ghost" size="xs" onClick={() => {
                                                let da: IdAndNameData<number> = {
                                                    id: entry.id,
                                                    name: `${hours} Stunden ab ${from}`
                                                };
                                                setDeleteData(da);
                                                onDeleteTimesheetBalanceOpen();
                                            }}>
                                                <LuTrash />
                                            </IconButton>
                                        </Tooltip>
                                    </Flex>
                                )}
                            </DataList.Item>
                        );
                    })}
                </DataList.Root>}
            {canEdit && (
                <>
                    <TimesheetBalanceDialog isOpen={isTimesheetBalanceOpen} onClose={onTimesheetBalanceClose} user={user} entry={editTimesheetBalanceData} onSave={saveTimesheetBalance} />
                    <ConfirmDialog
                        heading={`${deleteData?.name} Löschen`}
                        isOpen={isDeleteTimesheetBalanceOpen}
                        onClose={onDeleteTimesheetBalanceClose}
                        onConfirm={async (): Promise<void> => deleteTimesheetBalance(deleteData.id)}
                    />
                </>
            )}
        </>
    )
}

export default TimesheetBalanceSection;
